import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import client, { mediaUrl } from "../lib/api";
import Layout, { PageHero } from "../components/site/Layout";
import { SectionTitle, Reveal, EmptyState } from "../components/site/common";
import { Sparkles } from "lucide-react";
import { OBJECTIVES, PHOTOS } from "../lib/data";

export default function Initiatives() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { client.get("/public/initiatives").then(({ data }) => setItems(data)).catch(() => {}).finally(() => setLoaded(true)); }, []);
  return (
    <Layout>
      <PageHero
        eyebrow="Our Initiatives"
        title="Bringing faith and service together."
        intro="The Trust seeks to combine faith with service, compassion with responsibility, and tradition with meaningful community development."
      />

      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-24">
        <div className="grid gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-6">
            <div className="aspect-[4/3] overflow-hidden border border-gold/30">
              <img src={PHOTOS.hands} alt="Community support" className="h-full w-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-6">
            <SectionTitle eyebrow="Guiding Approach" title="Meaningful, dignified and rooted in compassion" intro="Guided by the belief that service to society is a meaningful expression of compassion, the Trust develops initiatives and partnerships that contribute positively to society while respecting India's spiritual, cultural and humanitarian traditions." />
          </div>
        </div>
      </section>

      <section className="bg-ivory-dark/40">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-24">
          <SectionTitle eyebrow="Our Initiatives" title="Programmes & activities" />
          <div className="mt-12">
            {loaded && items.length === 0 ? (
              <EmptyState icon={Sparkles} title="No initiatives published yet" message="Specific programmes, campaigns and activities undertaken by the Trust will be published here as they are established." />
            ) : (
              <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {items.map((it, i) => (
                  <Reveal key={it.id} delay={(i % 3) * 0.05}>
                    <div className="h-full border border-gold/20 bg-white" data-testid={`initiative-${it.id}`}>
                      {it.image && <div className="aspect-video overflow-hidden bg-ivory"><img src={mediaUrl(it.image)} alt={it.title} className="h-full w-full object-cover" /></div>}
                      <div className="p-7">
                        {it.category && <div className="eyebrow mb-2">{it.category}</div>}
                        <h3 className="text-xl font-medium text-maroon">{it.title}</h3>
                        <p className="mt-3 text-sm leading-relaxed text-ink-muted">{it.shortDescription || it.description}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="bg-white border-y border-gold/20">
        <div className="mx-auto max-w-7xl px-5 py-20 lg:px-10 lg:py-24">
          <SectionTitle eyebrow="Commitments" title="Principles shaping our initiatives" />
          <div className="mt-12 grid gap-x-12 gap-y-8 md:grid-cols-2">
            {OBJECTIVES.slice(0, 6).map(([title, desc], i) => (
              <Reveal key={title} delay={(i % 2) * 0.05}>
                <div className="border-t border-gold/20 pt-6">
                  <h3 className="text-lg font-medium text-maroon">{title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-ink-muted">{desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-10">
        <div className="border border-gold/25 bg-white p-10 text-center lg:p-16">
          <h2 className="text-3xl font-medium text-maroon lg:text-4xl">Interested in supporting our initiatives?</h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-ink-muted">We welcome individuals, organizations and well-wishers interested in contributing their time, expertise or support.</p>
          <Link to="/support" className="mt-8 inline-flex items-center gap-2 bg-maroon px-7 py-4 text-xs uppercase tracking-wide text-ivory transition-colors duration-300 hover:bg-maroon-dark">
            Register Your Interest <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </Layout>
  );
}
